import { useState } from 'react';
import { format, parseISO } from 'date-fns';

import { LogTimeline } from '@/components/scouting/LogTimeline';
import type { ComparisonPeriod } from '@/types/comparison';

interface ComparisonTimelineProps {
  periodA: ComparisonPeriod;
  periodB: ComparisonPeriod;
  onEntryClick: (id: string) => void;
}

function columnHeading(period: ComparisonPeriod, label: string): string {
  const start = parseISO(period.range.start);
  const end = parseISO(period.range.end);
  const count = period.entries.length;
  if (format(start, 'yyyy') === format(end, 'yyyy')) {
    return `${label} — ${format(start, 'MMM yyyy')} (${count} ${count === 1 ? 'entry' : 'entries'})`;
  }
  return `${label} — ${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')} (${count} ${count === 1 ? 'entry' : 'entries'})`;
}

type ActiveTab = 'a' | 'b';

export function ComparisonTimeline({
  periodA,
  periodB,
  onEntryClick,
}: ComparisonTimelineProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('a');

  const headingA = columnHeading(periodA, 'Period A');
  const headingB = columnHeading(periodB, 'Period B');

  // noop — comparison view is read-only, no adding entries from here
  function noop() {}

  return (
    <div data-testid="comparison-timeline">
      {/* Mobile: tabbed layout */}
      <div className="md:hidden">
        <div className="flex border-b border-[var(--color-border)] mb-4">
          <button
            type="button"
            onClick={() => setActiveTab('a')}
            data-testid="tab-period-a"
            className={`flex-1 py-2 text-body-sm font-medium transition-colors ${
              activeTab === 'a'
                ? 'text-[var(--color-accent)] border-b-2 border-[var(--color-accent)]'
                : 'text-[var(--color-text-muted)]'
            }`}
          >
            Period A ({periodA.entries.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('b')}
            data-testid="tab-period-b"
            className={`flex-1 py-2 text-body-sm font-medium transition-colors ${
              activeTab === 'b'
                ? 'text-[var(--color-accent)] border-b-2 border-[var(--color-accent)]'
                : 'text-[var(--color-text-muted)]'
            }`}
          >
            Period B ({periodB.entries.length})
          </button>
        </div>

        {activeTab === 'a' && (
          <div data-testid="mobile-panel-a">
            <p className="field-label mb-3">
              {headingA}
            </p>
            <LogTimeline
              entries={periodA.entries}
              onEntryClick={onEntryClick}
              onAddClick={noop}
            />
          </div>
        )}
        {activeTab === 'b' && (
          <div data-testid="mobile-panel-b">
            <p className="field-label mb-3">
              {headingB}
            </p>
            <LogTimeline
              entries={periodB.entries}
              onEntryClick={onEntryClick}
              onAddClick={noop}
            />
          </div>
        )}
      </div>

      {/* Desktop: side-by-side columns */}
      <div className="hidden md:grid md:grid-cols-2 md:gap-6" data-testid="desktop-columns">
        <div data-testid="desktop-panel-a">
          <p className="field-label mb-3">
            {headingA}
          </p>
          <LogTimeline
            entries={periodA.entries}
            onEntryClick={onEntryClick}
            onAddClick={noop}
          />
        </div>
        <div data-testid="desktop-panel-b">
          <p className="field-label mb-3">
            {headingB}
          </p>
          <LogTimeline
            entries={periodB.entries}
            onEntryClick={onEntryClick}
            onAddClick={noop}
          />
        </div>
      </div>
    </div>
  );
}
